import {DIALOG_SCROLL_STRATEGY} from '@angular/cdk/dialog';
import {Overlay, ScrollStrategy} from '@angular/cdk/overlay';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {inject, provideAppInitializer, provideZoneChangeDetection} from '@angular/core';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideRouter, withRouterConfig} from '@angular/router';
import {SHARED_ACCOUNT_INFORMATION} from '@truelearn-web/shared/account-information';
import {CONTENT_LIBRARY_ROOT_ROUTE} from '@truelearn-web/shared/content-library/tokens';
import 'bootstrap';
import {LDSingleKindContext} from 'launchdarkly-js-client-sdk';
import {en_US, NZ_I18N} from 'ng-zorro-antd/i18n';
import {filter, map} from 'rxjs/operators';

import {AppComponent} from 'apps/faculty/src/app/app.component';
import {mainRoutes} from 'apps/faculty/src/app/app-routing.module';
import {environment} from 'apps/faculty/src/environments/environment';
import {AM_CHARTS_CREDENTIALS, AmChartsCredentials} from 'libs/shared/environment/src/lib/tokens/am-charts.token';
import {APP_DATA, AppData} from 'libs/shared/environment/src/lib/tokens/app-data.token';
import {BULK_IMPORT_URLS, BulkImportUrls} from 'libs/shared/environment/src/lib/tokens/bulk-import-urls';
import {COOKIE_SETTINGS, CookieSettings} from 'libs/shared/environment/src/lib/tokens/cookie-settings.token';
import {PICMONIC_CONFIG, PicmonicConfig} from 'libs/shared/environment/src/lib/tokens/picmonic-config.token';
import {APP_URLS, AppUrls} from 'libs/shared/environment/src/lib/tokens/url.token';
import {FacultyAppService} from 'libs/shared/faculty/src/lib/service/faculty-app.service';
import {FacultyAppContextService} from 'libs/shared/faculty/src/lib/service/faculty-app-context.service';
import {FeatureFlagService} from 'libs/shared/feature-flag/src/lib/services/feature-flag/feature-flag.service';
import {
    FEATURE_FLAG_CREDENTIALS,
    FeatureFlagCredentials
} from 'libs/shared/feature-flag/src/lib/tokens/feature-flag-tokens';
import {AuthInterceptor} from 'libs/shared/http/src/lib/interceptors/auth-interceptor.service';
import {IdentityService} from 'libs/shared/http/src/lib/services/identity/identity.service';
import {bootstrapApplicationStandalone} from 'libs/shared/util/src/lib/_helpers/bootstrap-application';
import {firstValueFrom} from 'libs/shared/util/src/lib/_helpers/rxjs.future';

const picmonicConfig: PicmonicConfig = {
    EntrypointUrl: environment.PICMONIC_ENTRYPOINT_URL
};

async function initAppFactory(): Promise<void> {
    const featureFlagService = inject(FeatureFlagService);
    const appContextService = inject(FacultyAppContextService);
    const facultyAppService = inject(FacultyAppService);
    const identityService = inject(IdentityService);

    await identityService.asyncInit();
    await facultyAppService.initialize();
    await appContextService.initialize();
    const context = await firstValueFrom(
        appContextService.contextAccountInformation$.pipe(
            filter((x) => !!x?.Account?.Email),
            map((accountContext) => {
                const featureFlagContext: LDSingleKindContext = {
                    kind: 'user',
                    key: accountContext.Account.ExternalId,
                    email: accountContext.Account.Email,
                    institutionIds: [accountContext.Institution?.InstitutionId]
                };
                return featureFlagContext;
            })
        )
    );
    await featureFlagService.initialize(context);
}

function noopScrollStrategyFactory(overlay: Overlay): () => ScrollStrategy {
    return () => overlay.scrollStrategies.noop();
}

bootstrapApplicationStandalone(
    AppComponent,
    [
        provideZoneChangeDetection(),
        provideHttpClient(
            // DI-based interceptors must be explicitly enabled.
            // https://angular.dev/guide/http/interceptors#di-based-interceptors
            withInterceptorsFromDi()
        ),
        provideRouter(
            mainRoutes,
            withRouterConfig({
                onSameUrlNavigation: 'reload'
            })
        ),
        provideAnimations(),
        // Application scope includes the fullscreen question preview lazy route.
        {provide: CONTENT_LIBRARY_ROOT_ROUTE, useValue: '/content-library'},
        {
            provide: FEATURE_FLAG_CREDENTIALS,
            useValue: {
                CLIENT_SIDE_ID: environment.LAUNCH_DARKLY_CLIENT_SIDE_ID
            } as FeatureFlagCredentials
        },
        {
            provide: SHARED_ACCOUNT_INFORMATION,
            useFactory: (appContext: FacultyAppContextService) => appContext.getSharedAccountInformation(),
            deps: [FacultyAppContextService]
        },
        {
            provide: AM_CHARTS_CREDENTIALS,
            useValue: {AMCHARTS_KEY: environment.AMCHARTS_KEY} as AmChartsCredentials
        },
        {
            provide: APP_DATA,
            useValue: {
                NAME: environment.NAME,
                PRODUCTION: environment.PRODUCTION,
                SHOW_BANNER: environment.SHOW_BANNER,
                DEBUG_LOCALLY: environment.DEBUG_LOCALLY,
                PORTAL: 'faculty'
            } satisfies AppData
        },
        {
            provide: APP_URLS,
            useValue: {
                S3_BUCKET_URL: environment.S3_BUCKET_URL,
                API_URL: environment.API_URL,
                CLASSIC_URL: environment.CLASSIC_URL,
                DEBUG_URL: environment.DEBUG_URL,
                AUTH_URL: environment.AUTH_URL,
                AUTH_SERVER_URL: environment.AUTH_SERVER_URL,
                URL_TO_MEMBER: environment.URL_TO_MEMBER,
                COMQUEST_ENTRYPOINT_URL: undefined,
                ECOMMERCE_STORE_URL: undefined
            } satisfies AppUrls
        },
        {
            provide: BULK_IMPORT_URLS,
            useValue: {
                LEARNER_BULK_IMPORT_FILE_NAME: environment.LEARNER_BULK_IMPORT_FILE_NAME,
                ADMIN_LEARNER_BULK_IMPORT_FILE_NAME: environment.ADMIN_LEARNER_BULK_IMPORT_FILE_NAME,
                FACULTY_BULK_IMPORT_FILE_NAME: environment.FACULTY_BULK_IMPORT_FILE_NAME,
                ADMIN_FACULTY_BULK_IMPORT_FILE_NAME: environment.ADMIN_FACULTY_BULK_IMPORT_FILE_NAME
            } satisfies BulkImportUrls
        },
        {
            provide: COOKIE_SETTINGS,
            useValue: {
                COOKIE_DOMAIN: environment.COOKIE_DOMAIN,
                COOKIE_EXPIRY: environment.COOKIE_EXPIRY
            } satisfies CookieSettings
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {provide: NZ_I18N, useValue: en_US},
        provideAppInitializer(initAppFactory),
        {
            provide: DIALOG_SCROLL_STRATEGY,
            useFactory: noopScrollStrategyFactory,
            deps: [Overlay]
        },
        {
            provide: PICMONIC_CONFIG,
            useValue: picmonicConfig
        }
    ],
    environment
);
